import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopware-zu-shopify";
const TITLE = "Shopware zu Shopify wechseln: Ablauf, Kosten, Datenübernahme";
const DESCRIPTION =
  "Von Shopware 5 oder 6 zu Shopify wechseln: Was sich übernehmen lässt, welche Sonderfälle teuer werden, wie der Weiterleitungsplan aussieht und was der Umzug kostet.";
const UPDATED = "2026-09-12";

const ANSWER =
  "Ein Wechsel von Shopware zu Shopify läuft über vier Stufen: Export von Produkten, Kunden und Bestellungen, Nachbau der Kategorie- und Eigenschaftslogik in Shopify-Collections und Metafeldern, vollständiger 301-Plan für alle alten Adressen und ein gestaffelter Livegang. Typischer Zeitrahmen sind vier bis acht Wochen, je nach Schnittstellen und Preislogik.";

const faqs = [
  {
    q: "Lassen sich Shopware-Daten nach Shopify übernehmen?",
    a: "Produkte mit Varianten, Bilder, Kunden und Bestellhistorie lassen sich per CSV oder API übertragen. Shopware-Eigenschaften und Filter bilden Sie in Shopify über Collections, Tags und Metafelder nach, eine Eins-zu-eins-Übernahme gibt es dafür nicht.",
  },
  {
    q: "Was ist bei Shopware 5 anders als bei Shopware 6?",
    a: "Shopware 5 exportiert bequemer über Plugins, hat aber häufiger gewachsene Sonderlogik in Templates. Shopware 6 liefert saubere API-Exporte, dafür stecken Regeln oft im Rule Builder, der in Shopify über Rabattlogik, Kundengruppen oder Skripte neu abgebildet werden muss.",
  },
  {
    q: "Was passiert mit meinen Rankings?",
    a: "Rankings bleiben erhalten, wenn jede alte Shopware-Adresse per 301 ein passendes Ziel bekommt. Kritisch sind Kategorie-URLs mit Filterparametern und Hersteller-Seiten, die in Shopware oft eigene Sichtbarkeit haben.",
  },
  {
    q: "Was kostet der Wechsel von Shopware zu Shopify?",
    a: "Kleinere Sortimente ohne Schnittstellen liegen meist bei 5.000 bis 9.000 € netto. Mit ERP-Anbindung, B2B-Preislogik oder eigenem Konfigurator wird es deutlich mehr. Entwicklung berechne ich mit 110 € netto pro Stunde, Conversion-Optimierung ab 3.990 € netto.",
  },
  {
    q: "Wie lange dauert die Migration?",
    a: "Vier bis acht Wochen sind üblich. Der Aufwand hängt weniger an der Produktzahl als an Rule-Builder-Logik, Kundengruppen, Staffelpreisen und angebundenen Systemen.",
  },
];

export const Route = createFileRoute("/ratgeber/shopware-zu-shopify")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopify Migration",
      updated: UPDATED,
    }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Migration"
      title="Shopware zu Shopify: der Umzug ohne Sichtbarkeitsverlust"
      intro="Shopware ist mächtig und genau darin liegt der Aufwand: gewachsene Regeln, Eigenschaften und Plugins. Wer zu Shopify wechselt, tauscht Flexibilität gegen Betriebsruhe und sollte vorher wissen, welche Sonderfälle im Projekt teuer werden."
      source="ratgeber-shopware-zu-shopify"
      answer={ANSWER}
      updated={UPDATED}
      sections={[
        {
          h: "Warum Händler von Shopware wechseln",
          p: [
            "Der häufigste Grund ist nicht Unzufriedenheit mit dem Funktionsumfang, sondern der Betriebsaufwand. Updates, Hosting, Plugin-Kompatibilität und ein Ansprechpartner für Ausfälle binden Zeit, die im Verkauf fehlt.",
            "Shopify nimmt diesen Teil ab. Der Preis dafür ist weniger Freiheit in Checkout und Datenmodell, was bei Standardsortimenten selten stört und bei sehr spezieller Logik vorher geprüft gehört.",
          ],
        },
        {
          h: "Was sich sauber übernehmen lässt",
          p: [],
          list: [
            "Produkte mit Varianten, Bildern, Preisen und Beständen",
            "Kundenkonten, Passwörter werden neu vergeben",
            "Bestellhistorie als Referenzdaten",
            "Inhaltsseiten und Blogbeiträge",
          ],
        },
        {
          h: "Was neu gedacht werden muss",
          p: [
            "Shopware-Eigenschaften und Filter haben in Shopify keine direkte Entsprechung. Sie landen in Collections, Tags und Metafeldern, und diese Struktur entscheidet später über Filterbarkeit und SEO.",
          ],
          list: [
            "Eigenschaften und Filter auf Metafelder und Collections abbilden",
            "Rule-Builder-Regeln in Rabatt-, Kundengruppen- oder Skriptlogik überführen",
            "Staffel- und Nettopreise für B2B neu aufsetzen",
            "Hersteller-Seiten mit Rankings gezielt ersetzen",
          ],
        },
        {
          h: "Der Weiterleitungsplan",
          p: [
            "Hier entscheidet sich, ob Sichtbarkeit erhalten bleibt. Ziehen Sie alle Adressen aus Shopware und aus der Search Console, sortiert nach Sichtbarkeit und Umsatz, und geben Sie jeder einzelnen ein Ziel.",
          ],
          list: [
            "Produkt-, Kategorie- und Hersteller-Adressen einzeln weiterleiten",
            "Filter- und Seitenzahl-Adressen mit Rankings nicht übersehen",
            "Sammelweiterleitungen auf die Startseite vermeiden",
            "Weiterleitungen vor dem Livegang testen",
          ],
        },
        {
          h: "Kostenrahmen",
          p: [
            "Kleinere Sortimente ohne Schnittstellen liegen meist bei 5.000 bis 9.000 € netto, mit ERP-Anbindung und B2B-Logik deutlich darüber. Entwicklung rechne ich mit 110 € netto pro Stunde ab, Conversion-Optimierung ab 3.990 € netto. Nach Sichtung des Shops gibt es ein festes Angebot.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/ratgeber/shopify-migration", label: "Shopify Migration im Überblick" },
        { to: "/blog/woocommerce-zu-shopify-migration", label: "WooCommerce zu Shopify" },
        { to: "/ratgeber/shopify-b2b-shop-einrichten", label: "Shopify B2B einrichten" },
        { to: "/kontakt", label: "Migration anfragen" },
      ]}
    />
  );
}
